import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Button, IconButton, Tooltip } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useRef, useState } from "react";
import { TableColumn } from "react-data-table-component";
import { shallowEqual, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { RootState } from "../../../../redux";
import AlertDialog from "../../../../ui/components/alert-dialog/alert-dialog";
import AlertMessage from "../../../../ui/components/alert-message/alert-message";
import AutocompleteSelect from "../../../../ui/components/autocomplete-select/autocomplete-select";
import DateInputFilter from "../../../../ui/components/date-input-filter/date-input-filter";
import Table from "../../../../ui/components/table/table";
import { UserInfo } from "../../../../ui/models/userInfo";
import { ListModel } from "../../../models/list-model";
import { PracticaList } from "../../../models/practica-list";
import { practicaServices } from "../../../services/practica.services";
import { profesionalServices } from "../../../services/profesional.services";
import { getFormattedDate } from "../../../utils/common.utils";
import {
  formatearEstado,
  initialFilter,
  queryParamsConfig,
} from "../../../utils/practica.utils";
import { initialProfesionalFilter } from "../../../utils/profesional.utils";
import { useQueryParamsReplace } from "../../../utils/useQueryParamsReplace";
import PracticaAddModal from "../practica-add-modal/practica-add-modal";
import PracticaEditAdminModal from "../practica-edit-admin-modal/practica-edit-admin-modal";
import "./practicas-enviar.scss";

export default function PracticasEnviar() {
  const [queryParams, setQueryParams] =
    useQueryParamsReplace(queryParamsConfig);
  const [fetching, setFetching] = useState<boolean>(true);
  const [entities, setEntities] = useState<PracticaList[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { state, search } = location;
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAlertMessage, setShowAlertMessage] = useState<boolean>(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [textAlertModal, setTextAlertModal] = useState<string>("");
  const [practicaSelected, setPracticaSelected] = useState<PracticaList>();
  const [profesionalIdSelected, setProfesionalIdSelected] = useState<string>();
  const [profesionales, setProfesionales] = useState<ListModel[]>([]);
  const [queryParamsProfesional, setQueryParamsProfesional] = useState<any>();
  const [monthSelected, setMonthSelected] = useState<number>();
  const [yearSelected, setYearSelected] = useState<number>();
  const selectAllCheckboxRef = useRef<any>(null);
  const [allPageSelected, setAllPageSelected] = useState<boolean>(false);
  const [selectedItems, setSelectedItems] = useState<PracticaList[]>([]);
  const [showEnviarAlertModal, setShowEnviarAlertModal] = useState(false);
  const [textEnviarAlertModal, setTextEnviarAlertModal] = useState<string>("");

  const getProfesionales = async (searchValue: string) => {
    setQueryParamsProfesional({
      ...initialProfesionalFilter,
      filter: searchValue,
    });
    profesionalServices
      .lookup(queryParamsProfesional)
      .then((response) => {
        const items: ListModel[] = [];
        response.data.forEach((element: any) => {
          items.push({
            value: element.id,
            label: element.value + " " + element.description,
          });
        });
        setProfesionales(items);
      })
      .catch((error) => {});
  };

  const authInfo = useSelector<RootState, UserInfo>(
    ({ auth }: any) => auth.info,
    shallowEqual
  );

  useEffect(() => {
    if (location.search === "") {
      setQueryParams(initialFilter);
    }
    if (queryParams.page && authInfo) {
      getPracticas();
    }
  }, [queryParams, authInfo]);

  useEffect(() => {
    getPracticas();
  }, [profesionalIdSelected, monthSelected, yearSelected]);

  useEffect(() => {
    if (state?.textAlert) setShowAlertMessage(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    if (
      selectAllCheckboxRef !== null &&
      columns.length !== 0 &&
      entities.length > 0
    )
      updateSelectAllCheckbox();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems]);

  const updateSelectAllCheckbox = () => {
    if (selectedItems.length === entities.length) {
      selectAllCheckboxRef.current.indeterminate = false;
      setAllPageSelected(true);
    } else if (selectedItems.length === 0) {
      selectAllCheckboxRef.current.indeterminate = false;
      setAllPageSelected(false);
    } else {
      selectAllCheckboxRef.current.indeterminate = true;
      setAllPageSelected(false);
    }
  };

  useEffect(() => {
    if (authInfo) {
      if (authInfo.role === "ADMIN") {
        getProfesionales("");
      }
    }
  }, [authInfo]);

  const getPracticas = async () => {
    if (authInfo) {
      let params = { ...queryParams };
      if (profesionalIdSelected) {
        params = { ...params, profesionalId: profesionalIdSelected };
      }
      if (params.profesionalId && yearSelected && monthSelected !== undefined) {
        let fecha = new Date(yearSelected, monthSelected, 1);
        fecha.setUTCHours(0, 0, 0, 0);
        const fechaISO = fecha.toISOString();
        params = {
          ...params,
          mesAno: fechaISO,
          estado: "PENDIENTE",
        };

        setFetching(true);
        practicaServices
          .getList(params)
          .then((response) => {
            setFetching(false);
            setEntities(response.data.items);
            setTotalCount(response.data.totalItems);
            setAllPageSelected(false);
            setSelectedItems([]);
          })
          .catch((error) => {});
      } else {
        setEntities([]);
        setTotalCount(0);
      }
    }
  };

  const changeIsSelected = (status: boolean) => {
    entities.forEach((item) => {
      item.isSelected = status;
    });
  };

  const handleSelectAllCheckbox = () => {
    if (selectAllCheckboxRef.current?.checked) {
      changeIsSelected(true);
      setSelectedItems([...entities]);
    } else {
      changeIsSelected(false);
      setSelectedItems([]);
    }
  };

  const isRowSelected = (row: PracticaList) => {
    return selectedItems.includes(row);
  };

  const handleSelectRow = (row: PracticaList) => {
    if (isRowSelected(row)) {
      entities.forEach((item) => {
        if (item.id === row.id) item.isSelected = false;
      });
      setEntities([...entities]);
      setSelectedItems([...selectedItems.filter((s) => s.id !== row.id)]);
    } else {
      entities.forEach((item) => {
        if (item.id === row.id) item.isSelected = true;
      });
      setEntities([...entities]);
      setSelectedItems([...selectedItems, row]);
    }
  };

  const columns: TableColumn<PracticaList>[] = [
    {
      name: (
        <input
          ref={selectAllCheckboxRef}
          className="form-check-input form-check-primary"
          type="checkbox"
          name="select-all-checkbox"
          checked={allPageSelected}
          onChange={(e: any) => {
            e.stopPropagation();
            handleSelectAllCheckbox();
          }}
          onClick={(e: any) => {
            e.stopPropagation();
          }}
        />
      ),
      width: "55px",
      center: true,
      button: true,
      cell: (row: any) => (
        <input
          type="checkbox"
          className="form-check-input form-check-primary"
          name={`checkbox-${row.id}`}
          checked={row.isSelected}
          onChange={(e: any) => {
            e.stopPropagation();
            handleSelectRow(row);
          }}
          onClick={(e: any) => {
            e.stopPropagation();
          }}
        />
      ),
    },
    {
      name: "Profesional",
      selector: (row: any) => row.profesionalDescripcion,
      sortable: true,
      sortField: "profesional",
    },
    {
      name: "Obra Social",
      selector: (row) => row.obraSocialNombre,
      sortable: true,
      sortField: "obrasocial",
    },
    {
      name: "Fecha",
      selector: (row) => getFormattedDate(row.fecha.toString()),
      sortable: true,
      sortField: "fecha",
    },
    {
      name: "Paciente",
      selector: (row) => row.paciente,
      sortable: true,
      sortField: "paciente",
    },
    {
      name: "Nro. Validación Online",
      selector: (row) => row.nroValidacionOnline,
    },
    {
      name: "Código de Práctica",
      selector: (row) => row.practicaCodigo,
      wrap: true,
    },
    {
      name: "Descripcion",
      selector: (row) => row.practicaDescripcion,
    },
    {
      name: "Bono",
      selector: (row) => row.bono,
    },
    {
      name: "Cantidad",
      selector: (row) => row.cantidad,
    },
    {
      name: "Valor",
      selector: (row) => Number.parseFloat(row.precio.toString()).toFixed(2),
    },
    {
      name: "Valor Total",
      selector: (row) =>
        Number.parseFloat((row.precio * row.cantidad).toString()).toFixed(2),
      wrap: true,
    },
    {
      name: "Observacion",
      selector: (row) => row.observacion,
      wrap: true,
    },
    {
      name: "Estado",
      selector: (row) => formatearEstado(row.estado),
      wrap: true,
    },
    {
      name: "",
      button: true,
      cell: (row) => (
        <>
          {row.estado === "PENDIENTE" && (
            <Tooltip title="Modificar">
              <IconButton
                aria-label="modificar"
                onClick={() => handleShowEditModal(row)}
              >
                <EditOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}
          {row.estado === "PENDIENTE" && (
            <Tooltip title="Eliminar">
              <IconButton
                aria-label="delete"
                onClick={() => handleDeleteRow(row)}
              >
                <DeleteOutlineOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}
        </>
      ),
      width: "5%",
      style: {
        position: "sticky",
        right: 0,
        backgroundColor: "#fff",
        zIndex: 1,
      },
    },
  ];

  const handleDeleteRow = (practica: PracticaList) => {
    setShowAlertModal(true);
    setPracticaSelected(practica);
    setTextAlertModal(
      `Está por eliminar la práctica ${practica.practicaDescripcion} del paciente ${practica.paciente} del día ${getFormattedDate(practica.fecha.toString())}. ¿Desea continuar?`
    );
  };

  const handleShowAddModal = () => {
    setShowAddModal(true);
  };

  const handleCloseAddModal = (added: boolean, item: any) => {
    setShowAddModal(false);
    if (added) {
      getPracticas();
      navigate(
        {
          pathname: ".",
          search,
        },
        {
          state: {
            typeAlert: "success",
            textAlert: `Se agregó la práctica del paciente ${item.paciente}.`,
          },
        }
      );
    }
  };

  const deletePractica = () => {
    if (practicaSelected) {
      setFetching(true);
      practicaServices
        .delete(practicaSelected.id)
        .then((response) => {
          setShowAlertModal(false);
          getPracticas();
          navigate(
            {
              pathname: ".",
              search,
            },
            {
              state: {
                typeAlert: "success",
                textAlert: `Se eliminó la práctica del paciente ${response.data?.paciente}.`,
              },
            }
          );
        })
        .catch((error) => {});
    }
  };

  const handleShowEditModal = (practica: PracticaList) => {
    setShowEditModal(true);
    setPracticaSelected(practica);
  };

  const handleCloseEditModal = (added: boolean, item: any) => {
    setShowEditModal(false);
    if (added) {
      getPracticas();
      navigate(
        {
          pathname: ".",
          search,
        },
        {
          state: {
            typeAlert: "success",
            textAlert: `Se modificó la práctica del paciente ${item.paciente}.`,
          },
        }
      );
    }
  };

  const refetch = () => {
    getPracticas();
  };

  const ActionsComponent = () => {
    return (
      <>
        <Button
          className="close-button"
          onClick={(e) => {
            setShowAlertModal(false);
            setPracticaSelected(undefined);
          }}
        >
          Cancelar
        </Button>
        <Button
          className="create-button"
          variant="contained"
          onClick={() => deletePractica()}
        >
          Eliminar
        </Button>
      </>
    );
  };

  const EnviarActionsComponent = () => {
    return (
      <>
        <Button
          className="close-button"
          onClick={(e) => {
            setShowEnviarAlertModal(false);
          }}
        >
          Cancelar
        </Button>
        <Button
          className="create-button"
          variant="contained"
          onClick={() => enviarPracticas()}
        >
          Enviar
        </Button>
      </>
    );
  };

  const handleEnviarMultiple = () => {
    setShowEnviarAlertModal(true);
    setTextEnviarAlertModal(
      `Está por enviar ${selectedItems.length.toString()} práctica/s. ¿Desea continuar?`
    );
  };

  const enviarPracticas = () => {
    practicaServices
      .EnviarMultiple(selectedItems.map((i) => i.id))
      .then((response: any) => {
        getPracticas();
        navigate(
          {
            pathname: ".",
            search,
          },
          {
            state: {
              typeAlert: "success",
              textAlert:
                selectedItems.length === 1
                  ? "Se envió 1 práctica."
                  : `Se enviaron ${selectedItems.length.toString()} prácticas.`,
            },
          }
        );
        setShowEnviarAlertModal(false);
      })
      .catch((error: any) => {
        navigate(
          {
            pathname: ".",
            search,
          },
          {
            state: {
              typeAlert: "error",
              textAlert: `No se pudieron enviar las prácticas.`,
            },
          }
        );
        setShowAlertMessage(true);
      });
  };

  return (
    <div className="practicas-list-container">
      <AlertMessage
        text={state?.textAlert}
        type={state?.typeAlert}
        handleShowExternaly={true}
        show={showAlertMessage}
        setShow={setShowAlertMessage}
      />
      <div className="row action-button">
        <Button
          className="button"
          variant="contained"
          onClick={() => handleShowAddModal()}
        >
          Nueva Práctica
        </Button>
        <DateInputFilter
          name="mesAno"
          hiddenDay={true}
          onChange={(value: Dayjs) => {
            if (value !== null) {
              setMonthSelected(value.get("month"));
              setYearSelected(value.get("year"));
            } else {
              setMonthSelected(undefined);
              setYearSelected(undefined);
            }
          }}
          maxDate={dayjs()}
        />
        <AutocompleteSelect
          name="profesionalId"
          label="Profesional"
          isMultiple={false}
          options={profesionales}
          onChange={(value) => {
            setProfesionalIdSelected(value);
          }}
        ></AutocompleteSelect>
      </div>

      {entities.length > 0 && (
        <div className="row">
          <Button
            className="enviar-multiple-button"
            variant="outlined"
            color="success"
            onClick={handleEnviarMultiple}
            disabled={selectedItems.length === 0}
          >
            Enviar
          </Button>
        </div>
      )}

      <div className="row">
        <div className="practicas-table">
          {entities.length > 0 && <h2>Prácticas con estado Pendiente</h2>}
          <Table
            table={{
              columns,
              data: entities,
              paginationTotalRows: totalCount,
              pointerOnHover: true,
              highlightOnHover: true,
            }}
            setQueryParams={setQueryParams}
            queryParams={queryParams}
          ></Table>
        </div>
      </div>
      {!!showAddModal && (
        <PracticaAddModal
          show={showAddModal}
          handleClose={handleCloseAddModal}
          refetch={refetch}
        />
      )}
      {!!showAlertModal && (
        <AlertDialog
          title={"Advertencia"}
          text={textAlertModal}
          show={showAlertModal}
          setShow={setShowAlertModal}
          handleShowExternaly={true}
          actions={<ActionsComponent />}
        />
      )}
      {!!showEditModal && (
        <PracticaEditAdminModal
          show={showEditModal}
          practicaId={practicaSelected ? practicaSelected?.id : ""}
          handleClose={handleCloseEditModal}
          refetch={refetch}
        />
      )}
      {!!showEnviarAlertModal && (
        <AlertDialog
          title={"Advertencia"}
          text={textEnviarAlertModal}
          show={showEnviarAlertModal}
          setShow={setShowEnviarAlertModal}
          handleShowExternaly={true}
          actions={<EnviarActionsComponent />}
        />
      )}
    </div>
  );
}
